<template>
  <sp-card class="availability-panel" part="panel" variant="new-default" :data-testid="TestId.finder.availabilityPanel">
    <sp-card-content>
      <sp-text tag="h3" type="custom" class="availability-panel__heading" :class="breakpointModifiers" part="heading">{{
        label
      }}</sp-text>

      <!-- Each field's `--sp-comp-availability-filter-<field>-order` token sets its flex order. -->
      <div class="availability-panel__fields" part="fields">
        <AvailabilityOnlineBookingSwitch
          v-if="showOnlineBookingOption"
          :disabled="disabled"
          class="availability-panel__field --online-booking"
          part="online-booking-field"
          :checked="onlineBookingOnly"
          @update-checked="handleOnlineBookingUpdateChecked"
        />

        <AvailabilityVisitTypeSelect
          v-if="ownsVisitTypes"
          :disabled="disabled"
          class="availability-panel__field --visit-type"
          part="visit-type-field"
          :value="visitTypes"
          :nodes="visitTypeNodes"
          :any-value="ANY_VISIT_TYPE"
          :prompt="visitTypePrompt"
          :loading="visitTypesLoading"
          @select="handleVisitTypeSelect"
        />

        <AvailabilityTimeSelect
          :disabled="disabled"
          class="availability-panel__field --time"
          part="time-field"
          :value="availableTimes"
          @select="handleTimeSelect"
        />

        <AvailabilityDateSection
          :disabled="disabled"
          class="availability-panel__field --date"
          part="date-field"
          :mode="dateMode"
          :date="availableDate"
          :days="availableDays"
          :day-states="dayStates"
          :indicators="indicators"
          :today-link-href="todayLinkHref"
          @update-mode="handleDateModeUpdate"
          @select-date="handleDateSelect"
          @toggle-day="handleToggleDay"
          @visible-range="handleVisibleRange"
        />
      </div>
    </sp-card-content>
  </sp-card>
</template>

<script setup>
import { toBoolean } from "#ce/utils/props";
import { computed } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { TestId } from "#/test-ids";
import { ANY_VISIT_TYPE } from "#ce/shared/filters/visit-type/composables/useVisitTypeFilter";
import { t } from "#ce/shared/filters/availability/i18n";
import AvailabilityOnlineBookingSwitch from "#ce/shared/filters/availability/components/AvailabilityOnlineBookingSwitch.ce.vue";
import AvailabilityVisitTypeSelect from "#ce/shared/filters/availability/components/AvailabilityVisitTypeSelect.ce.vue";
import AvailabilityTimeSelect from "#ce/shared/filters/availability/components/AvailabilityTimeSelect.ce.vue";
import AvailabilityDateSection from "#ce/shared/filters/availability/components/AvailabilityDateSection.ce.vue";

const { breakpointModifiers } = useBreakpoints();

/**
 * `change` names the field the visitor changed: `online-booking`, `visit-type`, `time`, `date-mode`,
 * `date` or `day`. `visible-range` relays the calendar's rendered range.
 */
const emit = defineEmits(["change", "visible-range"]);

const props = defineProps({
  disabled: { type: Boolean, default: false },
  /** The filter's model, as `useAvailabilityFilterModel` returns it. The panel writes through its setters. */
  model: {
    type: Object,
    required: true,
  },
  /** The panel heading. */
  label: {
    type: String,
    required: true,
  },
  /** The visit-type tree `useVisitTypeFilter` builds. */
  visitTypeNodes: {
    type: Array,
    default: () => [],
  },
  visitTypesLoading: {
    type: Boolean,
    default: false,
  },
  /** Whether the visit-type select renders. */
  ownsVisitTypes: {
    type: Boolean,
    default: true,
  },
  /** Whether the online-booking switch renders. */
  showOnlineBookingOption: {
    type: Boolean,
    default: true,
  },
  /** The calendar's `sp-date-picker` day states. */
  dayStates: {
    type: Object,
    default: () => ({}),
  },
  /** Whether day indicators are fetched for the calendar. */
  indicators: {
    type: Boolean,
    default: false,
  },
  todayLinkHref: {
    type: String,
    default: undefined,
  },
});

// The model object never changes identity, so its refs are read once.
const {
  onlineBookingOnly,
  visitTypes,
  availableTimes,
  availableDate,
  availableDays,
  dateMode,
  setOnlineBookingOnly,
  setVisitTypes,
  setAvailableTimes,
  setDateMode,
  setAvailableDate,
  toggleDay,
} = props.model;

const visitTypePrompt = computed(() => t("visit_type_prompt"));

function handleOnlineBookingUpdateChecked(checked) {
  if (toBoolean(props.disabled)) {
    return;
  }
  setOnlineBookingOnly(checked);
  emit("change", "online-booking");
}

function handleVisitTypeSelect(value) {
  if (toBoolean(props.disabled)) {
    return;
  }
  setVisitTypes(value);
  emit("change", "visit-type");
}

function handleTimeSelect(value) {
  if (toBoolean(props.disabled)) {
    return;
  }
  setAvailableTimes(value);
  emit("change", "time");
}

function handleDateModeUpdate(mode) {
  if (toBoolean(props.disabled)) {
    return;
  }
  setDateMode(mode);
  emit("change", "date-mode");
}

function handleDateSelect(value) {
  if (toBoolean(props.disabled)) {
    return;
  }
  setAvailableDate(value);
  emit("change", "date");
}

function handleToggleDay(day, checked) {
  if (toBoolean(props.disabled)) {
    return;
  }
  toggleDay(day, checked);
  emit("change", "day");
}

function handleVisibleRange(range) {
  if (toBoolean(props.disabled)) {
    return;
  }
  emit("visible-range", range);
}
</script>

<style lang="scss" scoped>
.availability-panel {
  min-width: var(--sp-comp-availability-filter-panel-min-width, 18rem);

  // Padding is owned by the sp-card `new-default` variant.
  &__fields {
    display: flex;
    flex-direction: column;
    gap: var(--sp-comp-availability-filter-field-gap, var(--sp-ref-scale-16));
  }

  &__heading {
    --sp-comp-text-custom-font-short: var(
      --sp-comp-availability-filter-heading-font-short,
      var(--sp-sys-typescale-h3-short)
    );

    margin: 0 0 var(--sp-comp-availability-filter-heading-gap, var(--sp-ref-scale-16));

    &.--is-sm-screen {
      --sp-comp-text-custom-font-short: var(
        --sp-comp-availability-filter-heading-font-short,
        var(--sp-sys-typescale-h3-small-short, var(--sp-sys-typescale-h3-short))
      );
    }
  }

  // The defaults follow the finder page: the online-booking switch sits under the three selects.
  &__field {
    display: block;
    width: 100%;

    &.--online-booking {
      order: var(--sp-comp-availability-filter-online-booking-order, 4);
    }

    &.--visit-type {
      order: var(--sp-comp-availability-filter-visit-type-order, 1);
    }

    &.--time {
      order: var(--sp-comp-availability-filter-time-order, 2);
    }

    &.--date {
      order: var(--sp-comp-availability-filter-date-order, 3);
    }
  }
}
</style>
